/**
 * relicSceneManager — P3-04a / AR-R20
 * Single Responsibility: build and sync 3D shrine meshes for ARAM health relics.
 * Accepts THREE as injected dep for testability.
 *
 * Public API:
 *   getRelicVisualState(relic) → { color, opacity, lightIntensity }
 *   buildRelicMeshEntries(relics, THREE) → RelicEntry[]
 *   syncRelicMeshes(entries, relics) → void
 */

import {
  BoxGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  MeshStandardMaterial,
  PointLight
} from 'three';
import { buildRelicPadDisc, syncRelicPadDisc, RELIC_PAD_DISC_RADIUS } from './relicPadDisc.js';

const COLOR_ACTIVE = 0x22d3ee;
const COLOR_INACTIVE = 0x64748b;
const PEDESTAL_COLOR = 0x3f4a5a;
const LIGHT_ACTIVE = 0.85;
const LIGHT_INACTIVE = 0.15;
/** Vertical + horizontal bar sizes for Howling-Abyss-style cross. */
const CROSS_BAR_H = 0.95;
const CROSS_BAR_W = 0.28;
const CROSS_BAR_D = 0.28;

export function getRelicVisualState(relic) {
  const active = relic.active === true;
  return {
    active,
    color: active ? COLOR_ACTIVE : COLOR_INACTIVE,
    opacity: active ? 0.9 : 0.3,
    lightIntensity: active ? LIGHT_ACTIVE : LIGHT_INACTIVE
  };
}

function resolveClasses(THREE) {
  return {
    GroupCls: THREE?.Group || Group,
    BoxGeo: THREE?.BoxGeometry || BoxGeometry,
    CylinderGeo: THREE?.CylinderGeometry || CylinderGeometry,
    MeshMat: THREE?.MeshStandardMaterial || MeshStandardMaterial,
    MeshCls: THREE?.Mesh || Mesh,
    PointLt: THREE?.PointLight || PointLight
  };
}

function buildPedestal(MeshCls, CylinderGeo, MeshMat, relicId) {
  const geo = new CylinderGeo(0.35, 0.55, 0.4, 10);
  const mat = new MeshMat({ color: PEDESTAL_COLOR, roughness: 0.9 });
  const mesh = new MeshCls(geo, mat);
  mesh.name = `relic-pedestal-${relicId}`;
  mesh.position.set(0, 0.2, 0);
  return mesh;
}

function buildCrystal(MeshCls, BoxGeo, MeshMat, GroupCls, relicId, color, opacity) {
  const mat = new MeshMat({
    color,
    emissive: color,
    transparent: true,
    opacity
  });
  const cross = new GroupCls();
  cross.name = `relic-mesh-${relicId}`;
  cross.userData.relicShape = 'cross';
  const vertical = new MeshCls(new BoxGeo(CROSS_BAR_W, CROSS_BAR_H, CROSS_BAR_D), mat);
  vertical.name = `relic-cross-v-${relicId}`;
  const horizontal = new MeshCls(new BoxGeo(CROSS_BAR_H, CROSS_BAR_W, CROSS_BAR_D), mat);
  horizontal.name = `relic-cross-h-${relicId}`;
  cross.add(vertical);
  cross.add(horizontal);
  cross.position.set(0, 0.85, 0);
  return { mesh: cross, mat };
}

function attachShrineParts(group, pad, pedestal, mesh, light) {
  group.add(pad.mesh);
  group.add(pedestal);
  group.add(mesh);
  group.add(light);
}

function buildRelicShrine(relic, THREE) {
  const { GroupCls, BoxGeo, CylinderGeo, MeshMat, MeshCls, PointLt } =
    resolveClasses(THREE);
  const { color, opacity, lightIntensity } = getRelicVisualState(relic);
  const group = new GroupCls();
  group.name = `relic-${relic.id}`;
  const pedestal = buildPedestal(MeshCls, CylinderGeo, MeshMat, relic.id);
  const { mesh, mat } = buildCrystal(
    MeshCls, BoxGeo, MeshMat, GroupCls, relic.id, color, opacity
  );
  const pad = buildRelicPadDisc({
    MeshCls, CylinderGeo, MeshMat, relicId: relic.id, color, opacity: opacity * 0.7
  });
  const light = new PointLt(color, lightIntensity, 4);
  light.position.set(0, 1.1, 0);
  attachShrineParts(group, pad, pedestal, mesh, light);
  group.position.set(relic.x, 0, relic.z);
  return {
    relicId: relic.id,
    group,
    mesh,
    pedestal,
    pad: pad.mesh,
    padMat: pad.mat,
    padRadius: RELIC_PAD_DISC_RADIUS,
    light,
    mat
  };
}

export function buildRelicMeshEntries(relics, THREE) {
  return relics.map(relic => buildRelicShrine(relic, THREE));
}

export function syncRelicMeshes(entries, relics, { tSec = 0 } = {}) {
  entries.forEach(entry => {
    const relic = relics.find(r => r.id === entry.relicId);
    if (!relic) return;
    const { color, opacity, lightIntensity, active } = getRelicVisualState(relic);
    if (entry.mat) {
      if (entry.mat.color?.setHex) entry.mat.color.setHex(color);
      else entry.mat.color = color;
      if (entry.mat.emissive?.setHex) entry.mat.emissive.setHex(color);
      else entry.mat.emissive = color;
      entry.mat.opacity = opacity;
    }
    if (entry.mesh?.traverse) {
      entry.mesh.traverse((child) => {
        if (child.material && child.material !== entry.mat) {
          child.material.opacity = opacity;
        }
      });
    }
    if (entry.light) entry.light.intensity = lightIntensity;
    syncRelicPadDisc(entry, { color, opacity, active }, tSec);
  });
}
